// src/features/Models/useGetModelsService.ts
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { shuffle } from "lodash";

import { BackdropContext, HomeModelsContext } from "@contexts";
import { api } from "@utils";
import { getModels } from "./api";
import { GetModelsPayload, GetModelsResponse, Model } from "./types";

export const useGetModelsService = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    homeModels,
    setHomeModels,
    showModelsDisclaimer,
    setShowModelsDisclaimer,
  } = useContext(HomeModelsContext);
  const { setBackdropIsOpen } = useContext(BackdropContext);

  // Loading local (equivalente al isLoading que te daba useApiCall)
  const [isLoading, setIsLoading] = useState(false);

  // Mantener el backdrop sincronizado con isLoading local
  useEffect(() => {
    setBackdropIsOpen(isLoading);
  }, [isLoading, setBackdropIsOpen]);

  const checkRootPath = useCallback(() => {
    if (
      window.location.pathname !== "/" &&
      !window.location.pathname.startsWith("/admin")
    ) {
      navigate("/");
    }
  }, [navigate]);

  const handleSetHomeModels = useCallback(
    (models: Model[]) => {
      const vipModels = models.filter((m) => m.membership === "VIP");
      const topModels = models.filter((m) => m.membership === "TOP");
      const verifiedTrueModels = models.filter(
        (m) => m.membership === null && m.verified === true
      );
      const verifiedFalseModels = models.filter(
        (m) => m.membership === null && m.verified === false
      );

      const finalModelsArray = [
        ...shuffle(vipModels),
        ...shuffle(topModels),
        ...shuffle(verifiedTrueModels),
        ...shuffle(verifiedFalseModels),
      ];

      setHomeModels(finalModelsArray);
    },
    [setHomeModels]
  );

  // Fetcher con React Query (GET + cache por payload)
  const fetchModels = useCallback(
    async (payload: GetModelsPayload) => {
      return queryClient.fetchQuery({
        queryKey: ["models", payload],
        // IMPORTANTE: mismo fetch que antes pero envuelto para cachear
        queryFn: async () => {
          const { data } = await api.request<GetModelsResponse>(
            getModels(payload)
          );
          return data;
        },
        staleTime: 1000 * 60, // 1 minuto “fresh”
      });
    },
    [queryClient]
  );

  const handleGetModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchModels({});
      setShowModelsDisclaimer(false);
      handleSetHomeModels(response.models ?? []);
      checkRootPath();
    } catch (error) {
      console.error("Error fetching models:", error);
      // Manejo adicional si querés (toast, navigate, etc.)
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchModels,
    setShowModelsDisclaimer,
    handleSetHomeModels,
    checkRootPath,
  ]);

  const handleGetModelsByCity = useCallback(
    async (cityName: string) => {
      setIsLoading(true);
      try {
        const response = await fetchModels({ city: cityName });

        if (response.models?.length > 0) {
          setShowModelsDisclaimer(false);
          handleSetHomeModels(response.models);
        } else {
          // Sin resultados: traemos “all” y mostramos disclaimer
          const fallback = await fetchModels({});
          handleSetHomeModels(fallback.models ?? []);
          setShowModelsDisclaimer(true);
        }
        checkRootPath();
      } catch (error) {
        console.error("Error fetching models by city:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchModels, setShowModelsDisclaimer, handleSetHomeModels, checkRootPath]
  );

  return {
    handleGetModels,
    handleGetModelsByCity,
    homeModels,
    isLoading,
    showModelsDisclaimer,
  };
};
